import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import type { Label } from "features/labels/types";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useUpdateConversationParticipant from "common/datahooks/useUpdateConversationParticipant";
import useUpdateLead from "common/datahooks/useUpdateLead";
import { del, post } from "common/helpers/HTTP";
import type { FacetParam, Lead } from "common/types";

import useUpdateDetailedLead from "./useUpdateDetailedLead";

type UpdateCacheOn = "mutate" | "success";

interface LeadLabelMutationVariables {
  profileId: string;
  label: Label;
  abortSignal?: AbortSignal;
  updateCacheOn?: UpdateCacheOn;
}

async function addLabel(
  workspaceId: string,
  profileId: string,
  label: Label,
  abortSignal?: AbortSignal,
) {
  await post(
    `workspaces/${workspaceId}/leads/${profileId}/labels`,
    {
      labels: [label.id],
    },
    abortSignal,
  );
}
async function removeLabel(
  workspaceId: string,
  profileId: string,
  label: Label,
  abortSignal?: AbortSignal,
) {
  await del(
    `workspaces/${workspaceId}/leads/${profileId}/labels/${label.id}`,
    abortSignal,
  );
}

export default function useLeadLabelsMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { leadsKeys } = getQueryKeys(workspaceId);
  const { updateLead } = useUpdateLead();
  const { updateDetailedLead } = useUpdateDetailedLead();
  const { updateConversationParticipant } = useUpdateConversationParticipant();

  async function updateCachedLead(
    leadId: string,
    updateFunction: (lead: Pick<Lead, "labels">) => void,
  ) {
    await updateLead(leadId, updateFunction);
    await updateDetailedLead(leadId, updateFunction);
    await updateConversationParticipant(leadId, updateFunction);
  }

  async function applyAddLabelToCache(
    profileId: string,
    label: Label,
  ): Promise<boolean> {
    let didAddLabel = false;

    await updateCachedLead(profileId, (lead) => {
      const hasLabel = lead.labels.some(({ id }) => id === label.id);
      if (!hasLabel) {
        lead.labels.push(label);
        didAddLabel = true;
      }
    });

    if (didAddLabel) {
      queryClient.setQueriesData<FacetParam[]>(
        { queryKey: leadsKeys.facet("labels") },
        (queryData) => {
          return produce(queryData, (draftFacetData) => {
            for (let i = 0; i < draftFacetData.length; i += 1) {
              if (label.id === draftFacetData[i].id) {
                draftFacetData[i].target_count += 1;
                return;
              }
            }
            return [
              ...draftFacetData,
              { id: label.id, name: label.name, target_count: 1 },
            ];
          });
        },
      );
    }

    return didAddLabel;
  }

  async function applyRemoveLabelFromCache(profileId: string, label: Label) {
    await updateCachedLead(profileId, (lead) => {
      lead.labels = lead.labels.filter(({ id }) => id !== label.id);
    });

    queryClient.setQueriesData<FacetParam[]>(
      { queryKey: leadsKeys.facet("labels") },
      (queryData) => {
        return produce(queryData, (draftFacetData) => {
          for (let i = 0; i < draftFacetData.length; i += 1) {
            if (label.id === draftFacetData[i].id) {
              if (draftFacetData[i].target_count === 1) {
                return draftFacetData.filter(({ id }) => id !== label.id);
              } else {
                draftFacetData[i].target_count -= 1;
                return;
              }
            }
          }
        });
      },
    );
  }

  const { mutateAsync: addLeadLabel, isPending: isAddingLeadLabel } =
    useMutation({
      mutationFn: ({
        profileId,
        label,
        abortSignal,
      }: LeadLabelMutationVariables) =>
        addLabel(workspaceId, profileId, label, abortSignal),
      onMutate: async ({ profileId, label, updateCacheOn = "mutate" }) => {
        if (updateCacheOn === "mutate") {
          const didAddLabel = await applyAddLabelToCache(profileId, label);
          return { didAddLabel };
        }
        return { didAddLabel: false };
      },
      onSuccess: async (_, { profileId, label, updateCacheOn = "mutate" }) => {
        if (updateCacheOn === "success") {
          await applyAddLabelToCache(profileId, label);
        }
      },
      onError: async (
        _error,
        { profileId, label, abortSignal, updateCacheOn = "mutate" },
      ) => {
        if (updateCacheOn === "mutate") {
          await updateCachedLead(profileId, (lead) => {
            lead.labels = lead.labels.filter(({ id }) => label.id !== id);
          });
        }

        if (!abortSignal) {
          toast.error("Error adding label");
        }
      },
    });
  const { mutateAsync: removeLeadLabel, isPending: isRemovingLeadLabel } =
    useMutation({
      mutationFn: ({
        profileId,
        label,
        abortSignal,
      }: LeadLabelMutationVariables) =>
        removeLabel(workspaceId, profileId, label, abortSignal),
      onMutate: async ({ profileId, label, updateCacheOn = "mutate" }) => {
        if (updateCacheOn === "mutate") {
          await applyRemoveLabelFromCache(profileId, label);
        }
      },
      onSuccess: async (_, { profileId, label, updateCacheOn = "mutate" }) => {
        if (updateCacheOn === "success") {
          await applyRemoveLabelFromCache(profileId, label);
        }
      },
      onError: async (
        _error,
        { profileId, label, abortSignal, updateCacheOn = "mutate" },
      ) => {
        if (updateCacheOn === "mutate") {
          await updateCachedLead(profileId, (lead) => {
            lead.labels.push(label);
          });
        }

        if (!abortSignal) {
          toast.error("Error removing label");
        }
      },
    });
  return {
    addLeadLabel,
    isAddingLeadLabel,
    removeLeadLabel,
    isRemovingLeadLabel,
  };
}
